import { Component, input, inject, computed } from '@angular/core';

import { ApThemeService } from '@ap/theme';

import { availableMediaAssets } from './image.constant';
import { MediaAssetNames } from './image.type';

/**
 * @ngdoc component
 * @name ApMediaAssetNamesImageComponent
 * @module ApMediaAssetNamesModule
 * @description Media asset image component
 */
@Component({
    selector: 'ap-media-asset-image',
    templateUrl: './image.component.html',
    providers: [ApThemeService],
    styleUrl: './image.component.scss'
})
export class ApMediaAssetImageComponent {
    private readonly themeService = inject(ApThemeService);

    /**
     * Required - Name of the image to show.
     */
    public readonly name = input.required<MediaAssetNames>();

    /**
     * Required - The alternate text to add to the image. Make sure to add a relevant alt tag
     */
    public readonly alt = input.required<string>();

    /**
     * Optional - add height
     */
    public readonly height = input<string>();

    /**
     * Optional - add width
     */
    public readonly width = input<string>();

    /**
     * Url of asset to load
     * @internal
     */
    public readonly assetUrl = computed(() => this.getAssetUrl(this.name()));

    /**
     * Get correct asset url
     */
    private getAssetUrl(fileName: string): string {
        const staticUrl = this.themeService.getStaticUrl();
        const themedAsset = this.getThemedAsset(fileName);
        return `${staticUrl}/media-asset/${themedAsset}`;
    }

    /**
     * Return asset filename with optional theme if available
     */
    private getThemedAsset(assetName: string): string {
        const theme = this.themeService.getTheme();
        const themedAssetName = availableMediaAssets[theme]?.find((asset) => asset.startsWith(`${assetName}.`));
        const genericAssetName = availableMediaAssets.generic.find((asset) => asset.startsWith(`${assetName}.`));
        if (themedAssetName) {
            return `${themedAssetName}`;
        }
        return genericAssetName ? `${genericAssetName}` : `${assetName}`;
    }
}
